import { memo } from 'react';
import { Heart } from '@phosphor-icons/react/dist/ssr/Heart';
import ConnectionAvatar from '../UISystem/ConnectionAvatar';
import { COLORS } from '../../styles/UISystem';
import { formatRelativeTime } from '../../utils/dateHelpers';
import { findFirstUrl } from '../../services/posts/postsService';

/**
 * PostCard — one post in the feed (design: 2026-07-13_posts/posts-home.png).
 *
 * Content + heart only: no comment affordance, no airplane, no detail page in
 * Phase 1 (all arrive with posts-comments-v1). Author name/photo come live
 * from the feed RPC (profiles.name — never connection_views.display_name).
 *
 * @param {Object} post - Feed row from get_post_feed
 * @param {function} onToggleLike - Called with the post id on heart tap
 */

/**
 * Render post text with the detected URL as a tappable link. A URL-only post
 * (text is just the URL) renders as the link alone. The linked span comes
 * from the same finder the service used at post time, with trailing
 * punctuation rendered as plain text after the link.
 */
const renderText = (text, url) => {
  if (!text) return null;
  if (!url) return text;

  const found = findFirstUrl(text);
  if (!found) return text;

  return (
    <>
      {text.slice(0, found.index)}
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: COLORS.PRIMARY_BLUE, textDecoration: 'none', wordBreak: 'break-all' }}
        onClick={(e) => e.stopPropagation()}
      >
        {found.raw}
      </a>
      {text.slice(found.index + found.raw.length)}
    </>
  );
};

const PostCard = ({ post, onToggleLike }) => {
  const {
    author_name: authorName,
    author_photo_url: authorPhotoUrl,
    text,
    photo_url: photoUrl,
    url,
    location_snapshot: locationSnapshot,
    created_at: createdAt,
    like_count: likeCount,
    viewer_liked: viewerLiked,
  } = post;

  const timeAgo = formatRelativeTime(createdAt);
  const subtitle = [locationSnapshot, timeAgo].filter(Boolean).join(', ');

  return (
    <div
      style={{
        backgroundColor: COLORS.LIGHT_GRAY,
        borderRadius: '16px', // matches the 2026-07-13 mockup (photo-grid 12px precedent)
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        textAlign: 'left', // App.css centers globally — cards are left-aligned per design
      }}
    >
      {/* Author row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <ConnectionAvatar photoUrl={authorPhotoUrl} name={authorName} size={40} />
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: '15px',
              fontWeight: 600,
              color: '#000000',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {authorName}
          </div>
          {subtitle && (
            <div style={{ fontSize: '13px', color: '#8E8E93', fontStyle: 'italic' }}>
              {subtitle}
            </div>
          )}
        </div>
      </div>

      {/* Text (URL tappable) */}
      {text && (
        <div
          style={{
            fontSize: '16px',
            color: COLORS.DARK_GRAY,
            lineHeight: 1.4,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {renderText(text, url)}
        </div>
      )}

      {/* Photo — uniform 16:10 card crop (decided 2026-07-13 device review):
          consistent feed rhythm per the mockup's landscape frame. The stored
          image is untouched — the detail page (comments v1) shows it full. */}
      {photoUrl && (
        <img
          src={photoUrl}
          alt=""
          loading="lazy"
          decoding="async"
          style={{
            width: '100%',
            aspectRatio: '16 / 10',
            objectFit: 'cover',
            borderRadius: '12px',
            display: 'block',
          }}
        />
      )}

      {/* Heart + like count (count-only feedback — no notifications by design) */}
      <div
        onClick={() => onToggleLike(post)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          cursor: 'pointer',
          alignSelf: 'flex-start',
          padding: '2px 0',
        }}
      >
        <Heart
          size={22}
          weight={viewerLiked ? 'fill' : 'light'}
          color={viewerLiked ? '#FF3B30' : COLORS.DARK_GRAY}
        />
        {Number(likeCount) > 0 && (
          <span style={{ fontSize: '14px', color: COLORS.DARK_GRAY }}>{Number(likeCount)}</span>
        )}
      </div>
    </div>
  );
};

// memo pays here: a like toggle only changes one row's reference (applyLike
// keeps the others), and the page's onToggleLike is dep-stable — so one tap
// re-renders one card, not the whole feed.
export default memo(PostCard);
